/**
 * lib/financialStatements.ts
 * ------------------------------------------------------------------
 * THE TWO STATEMENTS A GOVERNING BODY READS.
 *
 * PURE. A trial balance in, a statement out. Built on glReport so there is
 * one set of arithmetic behind every figure the school publishes — a
 * statement that computes its own totals will eventually disagree with the
 * trial balance it came from, and the reader has no way to tell which is
 * wrong.
 *
 * INCOME & EXPENDITURE is what the school earned and spent between two
 * dates. BALANCE SHEET is what it owns and owes at a moment. The first
 * covers a period and resets each year; the second carries forward forever.
 *
 * THE UNCLOSED SURPLUS IS THE WHOLE DIFFICULTY. Income and expense accounts
 * hold this year's trading until the year-end close sweeps them into the
 * accumulated fund. Before that close, assets less liabilities does NOT
 * equal the equity accounts — it exceeds them by exactly the year's surplus,
 * because the surplus is real money sitting in the bank and in debtors with
 * nothing on the other side of the sheet claiming it yet.
 *
 * A balance sheet that ignores this is out by the surplus and looks broken.
 * One that hides the difference in a plug looks fine and lies. This one adds
 * the surplus to equity as its own named line — "Surplus for the year, not
 * yet closed" — which is both correct and legible: the reader sees the
 * figure, sees that it has not been closed, and can tie it to the income
 * statement beside it.
 *
 * CONTRA-REVENUE IS HANDLED BY THE SIGN, not by a special case. 4900
 * Discounts & Bursaries Granted is typed `income` and carries a DEBIT
 * balance, so signedBalance returns it negative and it reduces income
 * exactly as it should. A statement that special-cased it would be wrong the
 * day the school adds a second contra account.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { AccountType } from '@/lib/gl';
import {
  signedBalance, surplusForPeriod,
  type TrialBalance, type TrialBalanceRow,
} from '@/lib/glReport';

export interface StatementLine {
  code: string;
  name: string;
  /** On the line's own normal side — income positive, expense positive. */
  amount: number;
  entries: number;
}

export interface StatementSection {
  key: string;
  label: string;
  lines: StatementLine[];
  total: number;
}

/** Lines of one type, on their normal side, largest first. */
function sectionFor(
  rows: TrialBalanceRow[],
  type: AccountType,
  key: string,
  label: string,
): StatementSection {
  const lines = rows
    .filter((r) => r.type === type)
    .map((r) => ({
      code: r.code,
      name: r.name,
      amount: signedBalance(r.net, type),
      entries: r.entries,
    }))
    // An account that moved and came back to nil is not a line on a
    // statement; it is noise that makes a page harder to read.
    .filter((l) => l.amount !== 0)
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount) || a.code.localeCompare(b.code));

  return { key, label, lines, total: round2(lines.reduce((a, l) => a + l.amount, 0)) };
}

/* ==================== income & expenditure ==================== */

export interface IncomeStatement {
  income: StatementSection;
  expenditure: StatementSection;
  totalIncome: number;
  totalExpenditure: number;
  /** Income less expenditure. Negative is a deficit. */
  surplus: number;
  /** True when the school spent more than it earned. */
  isDeficit: boolean;
}

export function buildIncomeStatement(tb: TrialBalance): IncomeStatement {
  const income = sectionFor(tb.rows, 'income', 'income', 'Income');
  const expenditure = sectionFor(tb.rows, 'expense', 'expenditure', 'Expenditure');
  const { surplus } = surplusForPeriod(tb);
  return {
    income,
    expenditure,
    totalIncome: income.total,
    totalExpenditure: expenditure.total,
    surplus,
    isDeficit: surplus < 0,
  };
}

/* ========================= balance sheet ========================= */

export interface BalanceSheet {
  assets: StatementSection;
  liabilities: StatementSection;
  equity: StatementSection;
  totalAssets: number;
  totalLiabilities: number;
  /** Assets less liabilities — what the school is actually worth. */
  netAssets: number;
  /** The equity accounts as they stand in the ledger. */
  equityPosted: number;
  /** This year's surplus, still sitting in the income statement. */
  unclosedSurplus: number;
  /** equityPosted + unclosedSurplus. Should equal netAssets. */
  totalEquity: number;
  /** netAssets − totalEquity. Nil on a sound set of books. */
  difference: number;
  balanced: boolean;
}

export function buildBalanceSheet(tb: TrialBalance): BalanceSheet {
  const assets = sectionFor(tb.rows, 'asset', 'assets', 'Assets');
  const liabilities = sectionFor(tb.rows, 'liability', 'liabilities', 'Liabilities');
  const equity = sectionFor(tb.rows, 'equity', 'equity', 'Accumulated fund');

  const netAssets = round2(assets.total - liabilities.total);
  const { surplus } = surplusForPeriod(tb);
  const totalEquity = round2(equity.total + surplus);
  const difference = round2(netAssets - totalEquity);

  return {
    assets,
    liabilities,
    equity,
    totalAssets: assets.total,
    totalLiabilities: liabilities.total,
    netAssets,
    equityPosted: equity.total,
    unclosedSurplus: surplus,
    totalEquity,
    difference,
    // To the thebe. A balance sheet that does not balance is not a rounding
    // curiosity — every journal was asserted balanced before it was written.
    balanced: Math.abs(difference) < 0.005,
  };
}

/**
 * Why these figures should not be read as final, or null if they can be.
 *
 * Said once, at the top of the page, rather than left for the reader to
 * infer. A statement that quietly rests on a broken ledger is worse than no
 * statement: it carries the authority of a document without the arithmetic
 * behind it.
 */
export function statementCaveat(tb: TrialBalance, bs?: BalanceSheet): string | null {
  if (!tb.balanced) {
    return `The ledger is out of balance by ${Math.abs(tb.difference).toFixed(2)}. `
      + 'Do not report off these figures until that is found — every journal is '
      + 'checked before it is written, so this means one was written around the check '
      + 'or lines have been lost.';
  }
  if (bs && !bs.balanced) {
    return `Assets less liabilities differs from the fund by ${Math.abs(bs.difference).toFixed(2)}. `
      + 'The trial balance itself balances, so this is an account carrying the wrong '
      + 'type rather than a missing entry.';
  }
  if (!tb.rows.length) return 'Nothing has been posted for this period.';
  return null;
}
